<template>
    <a-modal
        v-model="visible"
        title="Etiket ve Renk Özelleştirme"
        width="500px"
        @ok="handleSave"
        :confirmLoading="saving"
        @close="handleClose"
        @cancel="handleClose"
    >
        <div class="label-customization">
            <div v-for="(item, key) in categoryItems" :key="key" class="label-item">
                <a-input v-model="item.label" :defaultValue="key" style="width: 200px" />
                <i class="material-icons arrow-icon">arrow_forward</i>
                <a-input v-model="item.color" type="color" style="width: 50px" />
            </div>
        </div>
    </a-modal>
</template>

<script>
import Vue from "vue";
import _ from "lodash";

export default Vue.extend({
    name: "LabelColorCustomization",
    props: {
        visible: {
            type: Boolean,
            required: true,
        },
        chartType: {
            type: String,
            required: true,
        },
        previewData: {
            type: Array,
            required: true,
        },
        fields: {
            type: Array,
            required: true,
        },
        settings: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            categoryItems: {},
            saving: false,
        };
    },
    methods: {
        handleClose() {
            this.$emit('close');
        },
        getRandomColor() {
            const letters = "0123456789ABCDEF";
            let color = "#";
            for (let i = 0; i < 6; i++) {
                color += letters[Math.floor(Math.random() * 16)];
            }
            return color;
        },
        getCalculationTypeLabel(type) {
            const labels = {
                count: 'Kayıt Sayısı',
                sum: 'Toplam',
                average: 'Ortalama',
                median: 'Medyan',
                min: 'Minimum',
                max: 'Maksimum'
            };
            return labels[type] || type;
        },
        initializeCategoryItems() {
            const items = {};
            if (!this.previewData || !this.previewData.length) return;

            const xAxisField = this.fields.find(f => f._id === this.$_.get(this.settings, 'xAxis.field'));

            if (this.isPieOrDonutChart) {
                // Pasta ve Halka grafikleri için X ekseni değerlerini kullan
                this.previewData.forEach(item => {
                    const value = item[xAxisField.name];
                    if (value !== undefined) {
                        const existingCustomization = this.$_.get(
                            this.settings,
                            `customization.labelCustomization.${value}`
                        );
                        items[value] = {
                            label: existingCustomization?.label || value,
                            color: existingCustomization?.color || this.getRandomColor()
                        };
                    }
                });
            } else if (this.isStackedChart) {
                // Stacked grafikler için stack gruplarını kullan
                const stackByField = this.fields.find(f => f._id === this.$_.get(this.settings, 'stackBy.field'));
                if (stackByField) {
                    this.previewData.forEach(item => {
                        Object.keys(item).forEach(key => {
                            if (key !== xAxisField.name && key !== 'value') {
                                const existingCustomization = this.$_.get(
                                    this.settings,
                                    `customization.labelCustomization.${key}`
                                );
                                items[key] = {
                                    label: existingCustomization?.label || key,
                                    color: existingCustomization?.color || this.getRandomColor()
                                };
                            }
                        });
                    });
                }
            } else if (this.isBubbleChart) {
                // Bubble grafik için tek bir renk
                const defaultColor = this.$_.get(
                    this.settings,
                    'customization.labelCustomization.default.color',
                    '#1890ff'
                );
                items['default'] = {
                    label: 'Kabarcık Rengi',
                    color: defaultColor
                };
            } else {
                // Diğer grafikler için (bar, line, area) tek bir renk
                const yAxisField = this.fields.find(f => f._id === this.$_.get(this.settings, 'yAxis.field'));
                const seriesName = yAxisField 
                    ? `${yAxisField.name} (${this.getCalculationTypeLabel(this.$_.get(this.settings, 'yAxis.calculationType'))})`
                    : 'Kayıt Sayısı';
                
                const existingCustomization = this.$_.get(
                    this.settings,
                    `customization.labelCustomization.default`
                );
                items['default'] = {
                    label: existingCustomization?.label || seriesName,
                    color: existingCustomization?.color || '#1890ff'
                };
            }

            this.categoryItems = items;
        },
        async handleSave() {
            try {
                this.saving = true;
                const customization = {};

                // Her kategori için label ve renk ayarlarını kaydet
                Object.entries(this.categoryItems).forEach(([key, value]) => {
                    if (key === 'default' && !this.isStackedChart && !this.isPieOrDonutChart) {
                        customization[key] = {
                            label: value.label,
                            color: value.color
                        };
                    } else {
                        customization[key] = {
                            label: value.label,
                            color: value.color
                        };
                    }
                });

                this.$emit('save', customization);
                this.$emit('update:visible', false);
            } catch (error) {
                console.error("Etiket özelleştirmeleri kaydedilirken hata:", error);
                this.$message.error("Etiket özelleştirmeleri kaydedilirken bir hata oluştu");
            } finally {
                this.saving = false;
            }
        },
    },
    computed: {
        isPieOrDonutChart() {
            return this.chartType === 'pie' || this.chartType === 'donut';
        },
        isStackedChart() {
            return (
                this.chartType &&
                (this.chartType === "stacked-bar" ||
                    this.chartType === "stacked-bar-100" ||
                    this.chartType === "stacked-area" ||
                    this.chartType === "stacked-area-100" ||
                    this.chartType === "stacked-line" ||
                    this.chartType === "stacked-line-100")
            );
        },
        isBubbleChart() {
            return this.chartType === 'bubble';
        }
    },
    watch: {
        previewData: {
            handler() {
                this.initializeCategoryItems();
            },
            immediate: true,
        },
        categoryItems: {
            handler(newItems) {
                // Renk değişikliklerini emit et
                const customization = {};
                Object.entries(newItems).forEach(([key, value]) => {
                    customization[key] = {
                        label: value.label,
                        color: value.color
                    };
                });
                this.$emit('change', customization);
            },
            deep: true
        },
    },
});
</script>

<style lang="scss" scoped>
.label-customization {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 400px;
    overflow-y: auto;
    padding-right: 16px;

    .label-item {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 8px 12px;
        background: #fafafa;
        border-radius: 4px;
        border: 1px solid #e8e8e8;

        .arrow-icon {
            color: rgba(0, 0, 0, 0.45);
            font-size: 20px;
        }

        .ant-input {
            font-size: 14px;

            &[type="color"] {
                padding: 2px;
                background: none;
                cursor: pointer;
                height: 32px;
            }
        }
    }
}
</style> 